import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // If returning from OAuth callback, AuthCallback handles it — skip me-check.
    const isOAuthCallback = typeof window !== "undefined" && window.location.hash?.includes("session_id=");
    let cancelled = false;
    (async () => {
      if (isOAuthCallback) {
        if (!cancelled) setLoading(false);
        return;
      }
      try {
        const { data } = await api.get("/auth/me");
        if (!cancelled) setUser(data);
      } catch {
        if (!cancelled) setUser(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Refresh user (incl. is_pro) when the tab regains focus — picks up server-side
  // changes like admin granting Pro or a friend coupon redemption without forcing
  // a hard refresh.
  useEffect(() => {
    const onFocus = () => {
      if (document.visibilityState === "visible" && localStorage.getItem("ff_token")) {
        checkAuth();
      }
    };
    document.addEventListener("visibilitychange", onFocus);
    window.addEventListener("focus", onFocus);
    return () => {
      document.removeEventListener("visibilitychange", onFocus);
      window.removeEventListener("focus", onFocus);
    };
  }, [checkAuth]);

  const loginWithEmail = async (email, password, deviceName) => {
    const { data } = await api.post("/auth/login", { email, password, device_name: deviceName || undefined });
    localStorage.setItem("ff_token", data.token);
    if (deviceName) localStorage.setItem("ff_device_name", deviceName);
    setUser(data.user);
    return data.user;
  };

  const registerWithEmail = async (email, password, name, deviceName) => {
    const { data } = await api.post("/auth/register", { email, password, name, device_name: deviceName || undefined });
    localStorage.setItem("ff_token", data.token);
    if (deviceName) localStorage.setItem("ff_device_name", deviceName);
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {
      // best-effort logout — keep going regardless
    }
    localStorage.removeItem("ff_token");
    localStorage.removeItem("ff_impersonator_token");
    setUser(null);
  };

  // --- Admin impersonation: swap to a customer JWT, keep a way back ---
  const impersonate = async (targetUserId) => {
    const { data } = await api.post(`/admin/users/${targetUserId}/impersonate`);
    const currentToken = localStorage.getItem("ff_token");
    if (currentToken) {
      localStorage.setItem("ff_impersonator_token", currentToken);
    }
    localStorage.setItem("ff_token", data.token);
    await checkAuth();
    return data.user;
  };

  const stopImpersonating = async () => {
    const original = localStorage.getItem("ff_impersonator_token");
    if (!original) return;
    localStorage.setItem("ff_token", original);
    localStorage.removeItem("ff_impersonator_token");
    await checkAuth();
  };

  const isImpersonating = typeof window !== "undefined" && !!localStorage.getItem("ff_impersonator_token");

  return (
    <AuthCtx.Provider value={{
      user, setUser, loading, loginWithEmail, registerWithEmail, logout,
      refresh: checkAuth, refreshUser: checkAuth, impersonate, stopImpersonating, isImpersonating,
    }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
